import { useRef, useState } from "react";
import { createContainer } from "unstated-next";
import { useRules } from "@/pages/profile/components/action-modal/change-account-modal/hooks/useRules";
import useForm from "antd/es/form/hooks/useForm";
import request from "project-config/services/request/request";
import CaptchaManager from "project-config/services/captcha";
import { useTranslation } from "react-i18next";
import { useGlobalStore } from "@/store/GlobalStore";
import { queryClient } from "@/index";
import { FormInstance, message as antdMessage } from "antd";

export enum CodeType {
  Sms = 1,
  Voice = 2,
  Email = -1,
}

function useModalFormHook() {
  const { t } = useTranslation();
  const rules = useRules();
  const [open, setOpen] = useState(false);
  const [openEmail, setOpenEmail] = useState(false);

  const [phoneForm] = useForm<{
    concat?: string;
    code?: string;
    areaCode?: string;
  }>();
  const [emailForm] = useForm<{ concat?: string; code?: string }>();
  const [sendCodeDisable, setSendCodeDisable] = useState(false);
  const [openStudentCertifyDrawer, setOpenStudentCertifyDrawer] =
    useState(false);
  const [openStudentVerifyConfirmModal, setOpenStudentVerifyConfirmModal] =
    useState(false);
  const [openUserChangeLogDrawer, setOpenUserChangeLogDrawer] = useState(false);
  const [openStudentToEnterpriseModal, setOpenStudentToEnterpriseModal] =
    useState(false);
  const [openStudentTipsModal, setOpenStudentTipsModal] = useState(false);

  const [successTip, setSuccessTip] = useState("");

  const timer = useRef<NodeJS.Timer>();
  const [countdown, setCountdown] = useState(0);
  const startCountDown = () => {
    if (timer.current) {
      clearInterval(timer.current);
    }
    setCountdown(60);
    timer.current = setInterval(() => {
      setCountdown((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          setSendCodeDisable(false);
          window.clearInterval(timer.current);
        }
        return next;
      });
    }, 1000);
  };

  const [sending, setSending] = useState(false);

  const { userInfo, fetchUserData, isDomestic } = useGlobalStore();
  const sendCode = async (form: FormInstance, codeType = CodeType.Sms) => {
    setSendCodeDisable(true);
    setSending(true);

    form
      .validateFields(["concat", "areaCode"])
      .then((values) => {
        const { concat, areaCode } = values;
        CaptchaManager.show(async (params?: object) => {
          if (params) {
            const url = "/api/rendering/user/sendEmailCode";
            // codeType === CodeType.Email
            //   ? "/api/rendering/user/sendEmailCode"
            //   : "/api/rendering/user/validate/SendVerifyCode";
            const param =
              codeType === CodeType.Email
                ? {
                    userName: userInfo?.userName,
                    email: form.getFieldValue("concat").toLowerCase(),
                    business: 8,
                    ...params,
                  }
                : {
                    type: codeType,
                    email: userInfo?.email
                      ? userInfo?.email.toLowerCase()
                      : userInfo?.email,
                    phone: areaCode ? `(${areaCode})${concat}` : concat,
                    business: 7,
                    userName: userInfo?.userName,
                    ...params,
                  };

            const { code, result, message } = await request.post(url, param);

            setSending(false);

            if (code === 200) {
              startCountDown();
            } else {
              antdMessage.error(t(`${message}`, { ns: "error" }));
              setSendCodeDisable(false);
            }
            if (result) {
              let message = "validate_code_send";
              if (
                !isDomestic &&
                [CodeType.Email, CodeType.Sms].includes(codeType)
              ) {
                message = "validate_code_send_fox";
              }
              antdMessage.success(t(`${message}`, { ns: "error" }));
            }
          } else {
            setSendCodeDisable(false);
            setSending(false);
          }
        });
      })
      .catch(() => {
        setSendCodeDisable(false);
        setSending(false);
      });
  };

  const changeConcatInfo = (form: FormInstance, type?: CodeType.Email) => {
    form
      .validateFields()
      .then(async (values) => {
        setConfirmLoading(true);
        const { concat, code, areaCode } = values;
        const url =
          type === CodeType.Email
            ? "/api/rendering/user/changeEmail"
            : "/api/rendering/user/changePhone";
        const param =
          type === CodeType.Email
            ? {
                email: concat?.toLowerCase(),
                verificationCode: code,
              }
            : {
                phone: areaCode ? `(${areaCode})${concat}` : concat,
                verificationCode: code,
                business: 7,
              };
        const { result, message } = await request.post(url, param);

        setConfirmLoading(false);

        if (!result) {
          antdMessage.error(t(`${message}`, { ns: "error" }));
          return;
        }
        antdMessage.success(t(`update_success`, { ns: "error" }));
        await queryClient.invalidateQueries("/api/rendering/user/queryUser");
        await queryClient.invalidateQueries("/api/sso/userInfo/getInfo");

        clearForm(form);

        setOpen(false);
        setOpenEmail(false);
        fetchUserData();
      })
      .catch(() => {
        setConfirmLoading(false);
      });
  };

  const clearForm = (form: FormInstance) => {
    form.setFieldsValue({ concat: "", code: "" });
    form.resetFields();

    setSending(false);
    setSuccessTip("");
    setConfirmLoading(false);
    window.clearInterval(timer.current);

    setCountdown(0);
    setSendCodeDisable(false);
  };

  const [confirmLoading, setConfirmLoading] = useState(false);
  return {
    open,
    setOpen,
    openEmail,
    setOpenEmail,
    rules,
    phoneForm,
    emailForm,
    sendCodeDisable,
    setSendCodeDisable,
    successTip,
    setSuccessTip,
    countdown,
    setCountdown,
    startCountDown,
    sending,
    setSending,
    sendCode,
    changeConcatInfo,
    clearForm,
    confirmLoading,
    setConfirmLoading,
    openStudentCertifyDrawer,
    setOpenStudentCertifyDrawer,
    openUserChangeLogDrawer,
    setOpenUserChangeLogDrawer,
    openStudentVerifyConfirmModal,
    setOpenStudentVerifyConfirmModal,
    openStudentToEnterpriseModal,
    setOpenStudentToEnterpriseModal,
    openStudentTipsModal,
    setOpenStudentTipsModal,
  };
}

const ModalFormStoreContainer = createContainer(useModalFormHook);
const ModalFormStoreProvider = ModalFormStoreContainer.Provider;

export default ModalFormStoreProvider;

export function useModalFormStore() {
  return ModalFormStoreContainer.useContainer();
}
